import { Constant } from "@constants/index";
import * as Yup from "yup";

export const loginSchema = Yup.object().shape({
    email: Yup.string()
        .required("Email is required")
        .matches(Constant.EMAIL_REGEX, "Please enter a valid email address without leading or trailing spaces")
        .max(50, "Email must contain at most 50 characters"),
    password: Yup.string().required("Password is required"),
});

export const signupSchema = Yup.object().shape({
    username: Yup.string()
        .required("Username is required")
        .min(3, "Username must contain at least 3 characters")
        .max(50, "Username must contain at most 50 characters"),
    email: Yup.string()
        .required("Email is required")
        .matches(Constant.EMAIL_REGEX, "Please enter a valid email address without leading or trailing spaces")
        .max(50, "Email must contain at most 50 characters"),
    password: Yup.string()
        .required("Password is required")
        .min(8, "Password must contain at least 8 characters")
        .max(50, "Maximum password length is 50 characters")
        .matches(/^[a-zA-Z0-9!@#$%^&*()_+{}[\]:;<>,.?~\\-]+$/, "Password must meet the complexity requirements")
        .matches(/^(?=.*[A-Z])/, "Password must contain at least one uppercase letter")
        .matches(/^(?=.*[a-z])/, "Password must contain at least one lowercase letter")
        .matches(/^(?=.*\d)/, "Password must contain at least one number")
        .matches(/^(?=.*[!@#$%^&*()_+{}[\]:;<>,.?~\\-])/, "Password must contain at least one special character"),
    confirmPassword: Yup.string()
        .required("Confirm password is required")
        .oneOf([Yup.ref("password"), ""], "Password must match"),
});

export const createCampaignSchema = Yup.object().shape({
    title: Yup.string().required("Campaign title is required")
        .max(50, "Campaign title must contain at most 50 characters"),
        // .matches(Constant.CAMPAIGN_TITLE_REGEX, "Campaign title must not contain special characters except hyphens"),
    description: Yup.string()
        .required("Description is required")
        .test("is-not-description", "Description is required", function (value) {
            const tempDiv = document.createElement("div");
            tempDiv.innerHTML = value;
            const hasImage = tempDiv.getElementsByTagName("img");
            const innerString = tempDiv.textContent || tempDiv.innerText;
            return innerString.trim().length > 0 || hasImage.length > 0;
        }),
        payment_description: Yup.string()
        .required(" Payment Description is required")
        .test("is-not-description", "Payment Description is required", function (value) {
            const tempDiv = document.createElement("div");
            tempDiv.innerHTML = value;
            const hasImage = tempDiv.getElementsByTagName("img");
            const innerString = tempDiv.textContent || tempDiv.innerText;
            return innerString.trim().length > 0 || hasImage.length > 0;
        }),
    goalAmount: Yup.number()
        .required("Amount goal is required")
        .max(Constant.CAMPAIGN_GOAL_AMOUNT_LIMIT, `Campaign goal amount cannot be more than ${Constant.CAMPAIGN_GOAL_AMOUNT_LIMIT}`),
    bannerImage: Yup.string().required("Banner image is required"),
    startDate: Yup.string().required("Start date is required"),
    endDate: Yup.string().required("End date is required"),
});

export const createReportSchema = Yup.object().shape({
    title: Yup.string().required("Report title is required").max(50, "Campaign title must contain at most 50 characters"),
    summary: Yup.string().nullable().max(255, "Summary must contain at most 255 characters"),
    fromYear: Yup.string()
        .required("Fiscal year(From) is required")
        .matches(/^\d{4}$/, "From year must be a 4-digit number")
        .test("fromYearRange", "Year must be valid", (value) => {
            const year = parseInt(value);
            return year >= 1900 && year <= 2099;
        }),
    toYear: Yup.string()
        .required("Fiscal year(To) is required")
        .matches(/^\d{4}$/, "To year must be a 4-digit number")
        .test("nextYear", "To year must be the next year of From year", (value, context) => {
            const fromYear = parseInt(context.parent.fromYear, 10);
            return parseInt(value, 10) === fromYear + 1;
        })
        .test("toYearRange", "Year must be valid", (value) => {
            const year = parseInt(value);
            return year >= 1900 && year <= 2099;
        }),
    fileSize: Yup.number().test("fileExists", "A file is required. Must be any of types including pdf, doc, docx, xls, and xlsx", (value) => {
        if (value && value > 0) return true;
        else return false;
    }),
});

export const editReportSchema = Yup.object().shape({
    title: Yup.string().nullable().max(50, "Campaign title must contain at most 50 characters"),
    summary: Yup.string().nullable().max(255, "Summary must contain at most 255 characters"),
    fromYear: Yup.string()
        .required("Fiscal year(From) is required")
        .matches(/^\d{4}$/, "From year must be a 4-digit number")
        .test("fromYearRange", "Year must be valid", (value) => {
            const year = parseInt(value);
            return year >= 1900 && year <= 2099;
        }),
    toYear: Yup.string()
        .required("Fiscal year(To) is required")
        .matches(/^\d{4}$/, "To year must be a 4-digit number")
        .test("nextYear", "To year must be the next year of From year", (value, context) => {
            const fromYear = parseInt(context.parent.fromYear, 10);
            return parseInt(value, 10) === fromYear + 1;
        })
        .test("toYearRange", "Year must be valid", (value) => {
            const year = parseInt(value);
            return year >= 1900 && year <= 2099;
        }),
    fileSize: Yup.number().test("fileExists", "A file is required. Must be any of types including pdf, doc, docx, xls, and xlsx", (value) => {
        if (value && value > 0) return true;
        else return false;
    }),
});

export const resetPasswordSchema = Yup.object({
    password: Yup.string()
        .required("Password is required")
        .min(8, "Password must contain at least 8 characters")
        .max(50, "Maximum password length is 50 characters")
        .matches(/^[a-zA-Z0-9!@#$%^&*()_+{}[\]:;<>,.?~\\-]+$/, "Password must meet the complexity requirements")
        .matches(/^(?=.*[A-Z])/, "Password must contain at least one uppercase letter")
        .matches(/^(?=.*[a-z])/, "Password must contain at least one lowercase letter")
        .matches(/^(?=.*\d)/, "Password must contain at least one number")
        .matches(/^(?=.*[!@#$%^&*()_+{}[\]:;<>,.?~\\-])/, "Password must contain at least one special character"),
    confirmPassword: Yup.string()
        .required("Reset password is required")
        .oneOf([Yup.ref("password"), ""], "Password must match"),
});

export const forgotPasswordSchema = Yup.object().shape({
    email: Yup.string()
        .required("Email is required")
        .matches(Constant.EMAIL_REGEX, "Please enter a valid email address without leading or trailing spaces")
        .max(50, "Email must contain at most 50 characters"),
});

export const editUserDetailsSchema = Yup.object().shape({
    email: Yup.string()
        .required("Email is required")
        .matches(Constant.EMAIL_REGEX, "Please enter a valid email address without leading or trailing spaces")
        .max(50, "Email must contain at most 50 characters"),
    username: Yup.string()
        .required("Username is required")
        .min(3, "Username must be more than 2 characters")
        .max(50, "Username must contain at most 50 characters"),
    name: Yup.string().nullable().min(3, "Name must be more than 2 characters").max(50, "Name must contain at most 50 characters"),
    phoneNumber: Yup.string()
        .nullable()
        .matches(/^[0-9]+$/, "Invalid phone number")
        .min(5, "Phone number must be more than 4 characters")
        .max(20, "Phone number must contain at most 20 characters"),
    role: Yup.string().required("Role of user is required"),
    address: Yup.string().nullable().max(255, "Address can be at most 255 characters long"),
    city: Yup.string().nullable().max(20, "City must contain at most 20 characters"),
    state: Yup.string().nullable().max(20, "State must contain at most 20 characters"),
    country: Yup.string().nullable().max(20, "Country must contain at most 20 characters"),
    zipCode: Yup.string()
        .nullable()
        .matches(/^[0-9]+$/, "Invalid zip code")
        .min(4, "Zip code must be more than 3 characters")
        .max(5, "Zip code must contain at most 5 characters"),
    communicationPreference: Yup.string().required("Communication preference of user is required"),
});

export const editUserProfileSchema = Yup.object().shape({
    email: Yup.string()
        .required("Email is required")
        .email("Please enter a valid email address without any leading or trailing spaces")
        .max(50, "Email must contain at most 50 characters"),
    username: Yup.string()
        .required("Username is required")
        .min(3, "Username must be more than 2 characters")
        .max(50, "Username must contain at most 50 characters"),
    name: Yup.string().nullable().min(3, "Name must be more than 2 characters").max(50, "Name must contain at most 50 characters"),
    phoneNumber: Yup.string()
        .nullable()
        .matches(/^[0-9]+$/, "Invalid phone number")
        .min(5, "Phone number must be more than 4 characters")
        .max(20, "Phone number must contain at most 20 characters"),
    address: Yup.string().nullable().max(255, "Address can be at most 255 characters long"),
    city: Yup.string().nullable().max(20, "City must contain at most 20 characters"),
    state: Yup.string().nullable().max(20, "State must contain at most 20 characters"),
    zipCode: Yup.string()
        .nullable()
        .matches(/^[0-9]+$/, "Invalid zip code")
        .min(4, "Zip code must be more than 3 characters")
        .max(5, "Zip code must contain at most 5 characters"),
});

export const createUserDetailsSchema = Yup.object().shape({
    email: Yup.string()
        .required("Email is required")
        .matches(Constant.EMAIL_REGEX, "Please enter a valid email address without leading or trailing spaces")
        .max(50, "Email must contain at most 50 characters"),
    password: Yup.string()
        .required("Password is required")
        .min(8, "Password must contain at least 8 characters")
        .max(50, "Maximum password length is 50 characters")
        .matches(/^[a-zA-Z0-9!@#$%^&*()_+{}[\]:;<>,.?~\\-]+$/, "Password must meet the complexity requirements")
        .matches(/^(?=.*[A-Z])/, "Password must contain at least one uppercase letter")
        .matches(/^(?=.*[a-z])/, "Password must contain at least one lowercase letter")
        .matches(/^(?=.*\d)/, "Password must contain at least one number")
        .matches(/^(?=.*[!@#$%^&*()_+{}[\]:;<>,.?~\\-])/, "Password must contain at least one special character"),
    username: Yup.string()
        .required("Username is required")
        .min(3, "Username must be more than 2 characters")
        .max(50, "Username must contain at most 50 characters"),
    name: Yup.string().nullable().min(3, "Name must be more than 2 characters").max(50, "Name must contain at most 50 characters"),
    phoneNumber: Yup.string()
        .nullable()
        .matches(/^[0-9]+$/, "Invalid phone number")
        .min(5, "Phone number must be more than 4 characters")
        .max(20, "Phone number must contain at most 20 characters"),
    role: Yup.string().required("Role of user is required"),
    address: Yup.string().nullable().max(255, "Address can be at most 255 characters long"),
    city: Yup.string().nullable().max(20, "City must contain at most 20 characters"),
    state: Yup.string().nullable().max(20, "State must contain at most 20 characters"),
    country: Yup.string().nullable().max(20, "Country must contain at most 20 characters"),
    zipCode: Yup.string()
        .nullable()
        .matches(/^[0-9]+$/, "Invalid zip code")
        .min(4, "Zip code must be more than 3 characters")
        .max(5, "Zip code must contain at most 5 characters"),
    communicationPreference: Yup.string().required("Communication preference of user is required"),
});

export const donorInfoFormSchema = Yup.object().shape({
    email: Yup.string()
        .required("Email is required")
        .matches(Constant.EMAIL_REGEX, "Please enter a valid email address without leading or trailing spaces")
        .max(50, "Email must contain at most 50 characters"),
    
    name: Yup.string()
        .required("Name is required")
        .min(3, "Name must be more than 2 characters")
        .max(50, "Name must contain at most 50 characters"),
    
    address: Yup.string()
        .required("Address is required")
        .max(255, "Address can be at most 255 characters long"),
    
    city: Yup.string()
        .required("District/City is required")
        .max(20, "District/City must contain at most 20 characters"),
    
    state: Yup.string()
        .nullable(" ")
        .max(20, "State must contain at most 20 characters"),
    
    country: Yup.string()
        .required("Country is required")
        .max(20, "Country must contain at most 20 characters"),
    
    zipCode: Yup.string()
    .nullable(" "),
        // .matches(/^[0-9]+$/, "Invalid zip code")
        // .min(4, "Post code must be more than 3 characters")
        // .max(5, "Post code must contain at most 5 characters"),
    
    phone_number: Yup.string()
        .required("Phone number is required"),
        // .matches(Constant.VALID_PHONE_NUMBER_REGEX, "Please enter a valid Phone number"),

    isOrganization: Yup.boolean(), // This tracks if checkbox is checked

    organizationName: Yup.string()
        .when("isOrganization", {
            is: true, // When the checkbox is checked
            then: (schema) => schema.required("Organization Name is required").max(100, "Organization Name must contain at most 200 characters"),
            otherwise: (schema) => schema.notRequired(), // Not required if unchecked
        }),
});
export const donorInfoFormSchemaBkash = Yup.object().shape({
    email: Yup.string()
        .required("Email is required")
        .matches(Constant.EMAIL_REGEX, "Please enter a valid email address without leading or trailing spaces")
        .max(50, "Email must contain at most 50 characters"),
    
    name: Yup.string()
        .required("Name is required")
        .min(3, "Name must be more than 2 characters")
        .max(50, "Name must contain at most 50 characters"),
    
    address: Yup.string()
        .nullable(" ")
        .max(255, "Address can be at most 255 characters long"),
    
    city: Yup.string()
        .nullable(" ")
        .max(20, "City must contain at most 20 characters"),
    
    state: Yup.string()
        .nullable(" ")
        .max(20, "State must contain at most 20 characters"),
    
    country: Yup.string()
         .nullable(" ")
        .max(20, "Country must contain at most 20 characters"),
    
    zipCode: Yup.string()
    .nullable(" "),
        // .matches(/^[0-9]+$/, "Invalid zip code")
        // .min(4, "Post code must be more than 3 characters")
        // .max(5, "Post code must contain at most 5 characters"),
    
    phone_number: Yup.string()
        .nullable(" "),
        // .matches(Constant.VALID_PHONE_NUMBER_REGEX, "Please enter a valid Phone number"),

    isOrganization: Yup.boolean(), // This tracks if checkbox is checked

    organizationName: Yup.string()
        .when("isOrganization", {
            is: true, // When the checkbox is checked
            then: (schema) => schema.required("Organization Name is required").max(100, "Organization Name must contain at most 200 characters"),
            otherwise: (schema) => schema.notRequired(), // Not required if unchecked
        }),
});
export const donorInfoFormSchemaNagad = Yup.object().shape({
    email: Yup.string()
        .required("Email is required")
        .matches(Constant.EMAIL_REGEX, "Please enter a valid email address without leading or trailing spaces")
        .max(50, "Email must contain at most 50 characters"),
    
    name: Yup.string()
        .required("Name is required")
        .min(3, "Name must be more than 2 characters")
        .max(50, "Name must contain at most 50 characters"),
    
    address: Yup.string()
        .nullable(" ")
        .max(255, "Address can be at most 255 characters long"),
    
    city: Yup.string()
        .nullable(" ")
        .max(20, "City must contain at most 20 characters"),
    
    state: Yup.string()
        .nullable(" ")
        .max(20, "State must contain at most 20 characters"),
    
    country: Yup.string()
         .nullable(" ")
        .max(20, "Country must contain at most 20 characters"),
    
    zipCode: Yup.string()
    .nullable(" "),
        // .matches(/^[0-9]+$/, "Invalid zip code")
        // .min(4, "Post code must be more than 3 characters")
        // .max(5, "Post code must contain at most 5 characters"),
    
    phone_number: Yup.string(),
    // .required("Phone number is required")
    //     .matches(Constant.VALID_PHONE_NUMBER_REGEX, "Please enter a valid Phone number"),

    isOrganization: Yup.boolean(), // This tracks if checkbox is checked

    organizationName: Yup.string()
        .when("isOrganization", {
            is: true, // When the checkbox is checked
            then: (schema) => schema.required("Organization Name is required").max(100, "Organization Name must contain at most 200 characters"),
            otherwise: (schema) => schema.notRequired(), // Not required if unchecked
        }),
});



export const loggedInDonorInfoFormSchema = Yup.object().shape({
    email: Yup.string(),
});

export const createPatientSchema = Yup.object().shape({
    name: Yup.string().required("Patient name is required").max(50, "Patient name must contain at most 50 characters"),
    email: Yup.string()
        .required("Email is required")
        .matches(Constant.EMAIL_REGEX, "Please enter a valid email address without leading or trailing spaces")
        .max(50, "Email must contain at most 50 characters"),
    phoneNumber: Yup.string().required("Phone number is required").max(20, "Phone number must contain at most 20 characters"),
    age: Yup.number().required("Age is required").max(Constant.PATIENT_AGE_LIMIT, `Patient age cannot be more than ${Constant.PATIENT_AGE_LIMIT}`),
    amountGoal: Yup.number()
        .required("Amount goal is required")
        .max(Constant.PATIENT_GOAL_AMOUNT_LIMIT, `Patient amount goal cannot be more than ${Constant.PATIENT_GOAL_AMOUNT_LIMIT}`),
});

export const createDeservingItemSchema = Yup.object().shape({
    name: Yup.string().required("Item name is required").max(50, "Item name must contain at most 50 characters"),
    description: Yup.string().required("Description is required"),
    amountGoal: Yup.number()
        .required("Amount goal is required")
        .max(Constant.DESERVING_ITEM_GOAL_AMOUNT_LIMIT, `Item amount goal cannot be more than ${Constant.DESERVING_ITEM_GOAL_AMOUNT_LIMIT}`),
});

export const datePickerSchema = Yup.object().shape({
    startDate: Yup.date().required("Start Date is required"),
    endDate: Yup.date().required("End Date is required").min(Yup.ref("startDate"), "End Date must be after Start Date"),
});
